import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageUploadBox } from '@service/seller/components/ImageUploadBox';
import {
  giftDeliveryCategoryDataAtomFamily,
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryStatusAtomFamily,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import {
  createGiftDeliveryCategory,
  updateGiftDeliveryCategory,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.controller';
import { useGiftDeliveryCategoryHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { TextField } from '@shared/components/TextField';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useNavigate, useParams } from 'react-router-dom';

export const GiftDeliveryCategoryFormPage: FC = function GiftDeliveryCategoryFormPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { sellerRole } = useAuth();

  const isEditMode = Boolean(categoryId);

  useGiftDeliveryCategoryHook(categoryId ?? '', sellerRole(), !isEditMode);
  const status = useRecoilValue(giftDeliveryCategoryStatusAtomFamily(categoryId ?? ''));
  const categoryData = useRecoilValue(
    giftDeliveryCategoryDataAtomFamily(categoryId ?? ''),
  );

  const setListData = useSetRecoilState(giftDeliveryCategoryListDataAtom);

  const [name, setName] = useState('');
  const [order, setOrder] = useState<number | ''>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode && categoryData) {
      setName(categoryData.name);
      setOrder(categoryData.order ?? '');
    }
  }, [isEditMode, categoryData]);

  const defaultImageUrl = useMemo(() => {
    if (categoryData?.image && typeof categoryData.image === 'object') {
      return (categoryData.image as Image).url;
    }
    return undefined;
  }, [categoryData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('카테고리명을 입력해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      let imageId: string | undefined;
      if (imageFile) {
        const uploaded = await postImage(imageFile, sellerRole());
        imageId = uploaded._id;
      }

      const payload = {
        name: name.trim(),
        ...(imageId && { image: imageId }),
        ...(order !== '' && { order: Number(order) }),
      };

      if (isEditMode && categoryId) {
        const updated = await updateGiftDeliveryCategory(
          categoryId,
          payload,
          sellerRole(),
        );
        setListData((prev) =>
          prev.map((item) => (item._id === categoryId ? updated : item)),
        );
        alert('수정되었습니다.');
      } else {
        const created = await createGiftDeliveryCategory(payload, sellerRole());
        setListData((prev) => [...prev, created]);
        alert('등록되었습니다.');
      }

      navigate('/gift-seller/delivery-category');
    } catch (error) {
      console.error(error);
      alert(isEditMode ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && (status === 'loading' || status === 'idle')) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
          >
            ←
          </button>
          <h1 className="text-xl font-bold">
            배송 카테고리 {isEditMode ? '수정' : '등록'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4">
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="space-y-4">
            <TextField
              label="카테고리명"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 신선식품, 가전, 생활용품"
              required
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                표시 순서
              </label>
              <input
                type="number"
                value={order}
                onChange={(e) =>
                  setOrder(e.target.value === '' ? '' : Number(e.target.value))
                }
                min={0}
                placeholder="미설정"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">낮을수록 앞에 표시됩니다.</p>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-base font-semibold">썸네일 이미지</h2>
          <div className="max-w-[300px]">
            <ImageUploadBox
              title="썸네일 이미지"
              setFile={setImageFile}
              defaultImageUrl={defaultImageUrl}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            카테고리 목록에서 사용되는 대표 썸네일 이미지입니다.
          </p>
        </div>

        <div className="mt-4 flex gap-3">
          <Button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700"
          >
            취소
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 rounded-lg bg-indigo-600 py-3 font-medium text-white disabled:opacity-50"
          >
            {isSubmitting ? '저장 중...' : isEditMode ? '수정하기' : '등록하기'}
          </Button>
        </div>
      </form>
    </div>
  );
};
